import type { StripeOperation, StripeOperationInfo, StripeCurrency } from '../types/stripe';

export const STRIPE_OPERATIONS: StripeOperation[] = [
	'create_payment_intent',
	'confirm_payment_intent',
	'refund_payment',
	'create_customer',
	'attach_payment_method',
	'create_subscription',
	'cancel_subscription',
	'create_invoice',
	'finalize_invoice',
	'send_invoice',
];

export const STRIPE_OPERATION_INFO: Record<StripeOperation, StripeOperationInfo> = {
	create_payment_intent: {
		operation: 'create_payment_intent',
		title: 'Create Payment Intent',
		description: 'Create a new payment intent to process a payment',
		requiredFields: ['amountPath'],
		optionalFields: ['customerIdPath', 'paymentMethodIdPath', 'confirm', 'captureMethod', 'setupFutureUsage'],
		webhookEvents: ['payment_intent.succeeded', 'payment_intent.payment_failed', 'payment_intent.requires_action'],
	},
	confirm_payment_intent: {
		operation: 'confirm_payment_intent',
		title: 'Confirm Payment Intent',
		description: 'Confirm a payment intent to complete the payment',
		requiredFields: ['paymentIntentIdPath'],
		optionalFields: ['paymentMethodIdPath', 'captureMethod'],
		webhookEvents: ['payment_intent.succeeded', 'payment_intent.payment_failed', 'payment_intent.requires_action'],
	},
	refund_payment: {
		operation: 'refund_payment',
		title: 'Refund Payment',
		description: 'Refund a payment using payment intent or charge ID',
		requiredFields: ['paymentIntentIdPath', 'chargeIdPath'], // One of these is required
		optionalFields: ['refundAmountPath'],
		webhookEvents: ['charge.refunded'],
	},
	create_customer: {
		operation: 'create_customer',
		title: 'Create Customer',
		description: 'Create a new customer in Stripe',
		requiredFields: ['customerEmailPath'],
		optionalFields: ['customerNamePath'],
		webhookEvents: ['customer.created'],
	},
	attach_payment_method: {
		operation: 'attach_payment_method',
		title: 'Attach Payment Method',
		description: 'Attach a payment method to a customer',
		requiredFields: ['attachCustomerIdPath', 'attachPaymentMethodIdPath'],
		optionalFields: ['setDefault'],
		webhookEvents: ['payment_method.attached'],
	},
	create_subscription: {
		operation: 'create_subscription',
		title: 'Create Subscription',
		description: 'Create a new subscription for a customer',
		requiredFields: ['subCustomerIdPath', 'priceIdPath'],
		optionalFields: ['trialDaysPath', 'cancelAtPeriodEnd'],
		webhookEvents: ['customer.subscription.created', 'customer.subscription.updated'],
	},
	cancel_subscription: {
		operation: 'cancel_subscription',
		title: 'Cancel Subscription',
		description: 'Cancel an existing subscription',
		requiredFields: ['subscriptionIdPath'],
		optionalFields: ['cancelAtPeriodEnd'],
		webhookEvents: ['customer.subscription.deleted'],
	},
	create_invoice: {
		operation: 'create_invoice',
		title: 'Create Invoice',
		description: 'Create a new invoice for a customer',
		requiredFields: ['invoiceCustomerIdPath'],
		optionalFields: ['invoiceAutoAdvance', 'sendToEmailPath'],
		webhookEvents: ['invoice.created'],
	},
	finalize_invoice: {
		operation: 'finalize_invoice',
		title: 'Finalize Invoice',
		description: 'Finalize a draft invoice',
		requiredFields: ['invoiceIdPath'],
		optionalFields: [],
		webhookEvents: ['invoice.finalized'],
	},
	send_invoice: {
		operation: 'send_invoice',
		title: 'Send Invoice',
		description: 'Send an invoice to a customer',
		requiredFields: ['invoiceIdPath'],
		optionalFields: ['sendToEmailPath'],
		webhookEvents: ['invoice.sent'],
	},
};

export const STRIPE_CURRENCIES: StripeCurrency[] = [
	{ code: 'usd', name: 'US Dollar', symbol: '$' },
	{ code: 'eur', name: 'Euro', symbol: '€' },
	{ code: 'gbp', name: 'British Pound', symbol: '£' },
	{ code: 'cad', name: 'Canadian Dollar', symbol: 'C$' },
	{ code: 'aud', name: 'Australian Dollar', symbol: 'A$' },
	{ code: 'jpy', name: 'Japanese Yen', symbol: '¥' },
	{ code: 'chf', name: 'Swiss Franc', symbol: 'CHF' },
	{ code: 'sek', name: 'Swedish Krona', symbol: 'kr' },
	{ code: 'nok', name: 'Norwegian Krone', symbol: 'kr' },
	{ code: 'dkk', name: 'Danish Krone', symbol: 'kr' },
];

export const DEFAULT_CURRENCY = 'usd';
export const DEFAULT_TIMEOUT_SEC = 900;
export const DEFAULT_CAPTURE_METHOD = 'automatic';
export const DEFAULT_SETUP_FUTURE_USAGE = 'off_session';

export const STRIPE_ACCOUNT_MODES = [
	{ value: 'platform', label: 'Platform Account', description: 'Use your main Stripe account' },
	{ value: 'connect', label: 'Connect Account', description: 'Use a connected Stripe account' },
] as const;

export const CAPTURE_METHODS = [
	{ value: 'automatic', label: 'Automatic', description: 'Capture payment immediately' },
	{ value: 'manual', label: 'Manual', description: 'Capture payment later' },
] as const;

export const SETUP_FUTURE_USAGE_OPTIONS = [
	{ value: 'off_session', label: 'Off Session', description: 'Use for future off-session payments' },
	{ value: 'on_session', label: 'On Session', description: 'Use only for current session' },
] as const;
